import React, { useState } from "react";
import { useSelector } from "react-redux";
import { extentions } from "../../utils/constants";
import PortalTooltip from "./PortalTooltip";

const ProductListType = ({ product, stock, findStoreStock }) => {
  const is_pin_b = useSelector((state) => state?.doctor?.doctorDetails?.doctor?.is_pin_b);
  const productStock = findStoreStock(product) || stock || 0;
  const [showAllDrugs, setShowAllDrugs] = useState(false);

  const mrp = product?.pricing?.mrp || product?.mrp || 0;
  const discount = product?.lookupMargin?.discount || product?.discount || 0;

  const discountAmount = ((mrp * discount) / 100).toFixed(2);
  const netAmt = (mrp - discountAmount).toFixed(2);

  return (
    <>
      <div className="flex items-start cursor-pointer flex-1 min-w-0">
        <div className="text-left w-full min-w-0">
          <PortalTooltip text={product?.productName}>
            <h5 className="text-m font-semibold text-primary-700 truncate">
              {product?.productName || ""}
            </h5>
          </PortalTooltip>
          <p className="text-xs text-primary-500">
            {product?.type} • {product?.uom || product?.product?.uom} {extentions[product?.type]?.uomExt || extentions[product?.type]?.extention || ""} {is_pin_b && product?.lookupMargin?.type && `• ${product?.lookupMargin?.type}`} • <span className={`${Number(productStock) > 6
                ? "font-medium text-success-600"
                : Number(productStock) === 0
                  ? "font-medium text-primary-600"
                  : "font-medium text-yellow-700"
              }`}>Stock: {Number(productStock)}</span>
              { (product?.isCustom || String(product?.IID || product?.CPID).startsWith("CP") || (product?.IID === null && Number(product?.orderQty || product?.qty) === 0)) && (
                <span className="ml-2 bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase border border-amber-200">
                  Custom
                </span>
              )}
          </p>
          <div className="flex items-center gap-2 mt-1">
            {product?.productName && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllDrugs(!showAllDrugs);
                }}
                className="text-primary-500 text-[10px] uppercase font-semibold bg-primary-50 px-1.5 py-0.5 rounded border border-primary-100 hover:bg-primary-100 transition-colors whitespace-nowrap flex-shrink-0 cursor-pointer"
              >
                INFO
              </button>
            )}
            <p className="text-[11px] text-primary-400 truncate flex-1 max-w-[140px] italic">
              {product?.drugs && product?.drugs.length > 0
                ? product?.drugs
                  .slice(0, 3)
                  .map((drug, index) => drug?.name ? drug?.name : drug)
                  .join(", ") +
                (product?.drugs.length > 3 ? "..." : "")
                : ""}
            </p>
          </div>
        </div>
      </div>

      {!product?.isCustom && (
        <div className="flex items-center gap-3 h-full flex-shrink-0">
          <div className="flex flex-col items-end">
            <span className="text-[15px] font-semibold text-primary-700 leading-tight">
              ₹{parseFloat(netAmt || 0).toFixed(2)}
            </span>
            <span className="text-[11px] text-primary-500 line-through">
              ₹{parseFloat(mrp || 0).toFixed(2)}
            </span>
            <span className="text-[11px] font-semibold text-success-600">
              {Math.round(discount || 0)}% off
            </span>
          </div>
        </div>
      )}

      {/* Drugs Modal/Popover */}
      {showAllDrugs && (() => {
        const manufacturerVal = product?.manufacturer || product?.product?.manufacturer || "";
        const stockVal = productStock;
        const uomVal = product?.uom || product?.product?.uom || 1;
        const uomType = extentions[product?.type]?.uomExt || extentions[product?.type]?.extention || "";

        return (
          <div 
            onClick={(e) => {
              e.stopPropagation();
              setShowAllDrugs(false);
            }} 
            className="fixed inset-0 bg-[#0a1128]/35 backdrop-blur-xs flex items-center justify-center z-[9999] p-4 transition-all duration-300"
          >
            <div 
              onClick={(e) => e.stopPropagation()} 
              className="bg-white/95 backdrop-blur-lg border border-white/20 rounded-2xl p-6 max-w-md w-full shadow-2xl relative max-h-[85vh] overflow-y-auto animate-scale-in"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4 pb-2 border-b border-primary-100/60">
                <div>
                  <h3 className="text-[10px] font-bold text-primary-400 uppercase tracking-wider">Product Information</h3>
                  <h2 className="text-base font-extrabold text-primary-950 mt-0.5 leading-snug">{product?.productName || "Product Detail"}</h2>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllDrugs(false);
                  }}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 hover:bg-primary-100 text-primary-500 hover:text-primary-700 transition-all font-bold text-sm shadow-3xs cursor-pointer"
                >
                  ✕
                </button>
              </div>

              {/* Grid Specs */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="p-3 bg-primary-50/40 rounded-xl border border-primary-100/20">
                  <span className="block text-[9px] font-bold text-primary-400 uppercase tracking-wider">Type</span>
                  <span className="text-[12px] font-extrabold text-primary-800">{product?.type || "N/A"}</span>
                </div>
                <div className="p-3 bg-primary-50/40 rounded-xl border border-primary-100/20">
                  <span className="block text-[9px] font-bold text-primary-400 uppercase tracking-wider">Packaging (UOM)</span>
                  <span className="text-[12px] font-extrabold text-primary-800">
                    {uomVal} {uomType}
                  </span>
                </div>
                <div className="p-3 bg-primary-50/40 rounded-xl border border-primary-100/20">
                  <span className="block text-[9px] font-bold text-primary-400 uppercase tracking-wider">Store Stock</span>
                  <span className={`text-[12px] font-extrabold ${Number(stockVal) > 5 ? 'text-green-600' : 'text-amber-600'}`}>
                    {stockVal} units
                  </span>
                </div>
                <div className="p-3 bg-primary-50/40 rounded-xl border border-primary-100/20">
                  <span className="block text-[9px] font-bold text-primary-400 uppercase tracking-wider">Manufacturer</span>
                  <span className="text-[12px] font-extrabold text-primary-800 truncate block" title={manufacturerVal}>{manufacturerVal || "N/A"}</span>
                </div>
              </div>

              {/* Drug Composition */}
              {product?.drugs && product?.drugs.length > 0 && (
                <div>
                  <h4 className="text-[9px] font-bold text-primary-400 uppercase tracking-wider mb-2">Salt Composition</h4>
                  <div className="space-y-1.5">
                    {product?.drugs.map((drug, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 bg-primary-50/60 rounded-lg text-primary-700 text-xs font-bold border border-primary-100/40 shadow-3xs"
                      >
                        {drug?.name ? drug?.name : drug}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default ProductListType;
